import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { TablePriceDisplay } from '@/components/ui-v2/inputs/table/table-price-display';
import { TablePriceInput } from '@/components/ui-v2/inputs/table/table-price-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import {
  cn,
  formatCurrency,
  getCurrentValue,
  getDeltaSign,
  getDeltaValue,
  getMyLastValue,
  getVatLabelKey,
  hasPreviousVersion,
} from '@/lib/utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { storeAtom } from '@/states/store.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon, TrendingDown, TrendingUp } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface ShippingPriceCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function ShippingPriceCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ShippingPriceCellContentProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const user = useUser();
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'shippingPrice');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.shippingPrice?.toFixed(2);
    if (typeof value === 'undefined' || value === null) return null;
    return `${formatCurrency(parseFloat(value), store)}`;
  };

  const getParentValue = () => {
    if (
      !row.subRows.every(
        (subRow) =>
          typeof subRow.original.activeVersion.shippingPrice === 'number'
      )
    ) {
      return null;
    }
    const subRowsValues: number[] = row.subRows.map((subRow) => {
      const value = subRow.original.activeVersion.shippingPrice!.toFixed(2);
      return parseFloat(value);
    });
    if (subRowsValues.length === 0) return null;
    const min = Math.min(...subRowsValues);
    const max = Math.max(...subRowsValues);
    return min === max
      ? `${formatCurrency(min, store)}`
      : `${formatCurrency(min, store)} - ${formatCurrency(max, store)}`;
  };

  const errorState = getTableCellErrorState({
    row,
    field: 'shippingPrice',
    errorType: 'empty',
    conditionalLogic: { checkDigitalProduct: true },
  });

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="shippingPrice"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="h-full w-full">
          {isEditing ? (
            <TablePriceInput
              value={row.original.activeVersion.shippingPrice ?? null}
              onChange={async (value) => {
                await handleCellEdit(
                  row.original.activeVersion.id,
                  'shippingPrice',
                  value,
                  !isParent
                );
              }}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={isOfferLocked}
              autoFocus={isEditing}
              hasError={errorState.hasError}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="h-full w-full"
                  onClick={() =>
                    !isParent &&
                    handleCellClick(
                      row.original.activeVersion.id,
                      'shippingPrice'
                    )
                  }
                >
                  <TablePriceDisplay
                    value={
                      errorState.isEmpty
                        ? null
                        : isParent
                          ? getParentValue()
                          : getValue()
                    }
                    isEmpty={errorState.isEmpty}
                    hasError={errorState.hasError}
                    errorMessage={errorState.errorMessage}
                    isEditable={!isOfferLocked}
                    isParent={isParent}
                    isRecentlyModified={isRecentlyModified}
                  >
                    {!isParent &&
                      user?.role &&
                      hasPreviousVersion(
                        row.original,
                        'shippingPrice',
                        user?.role
                      ) &&
                      getDeltaSign(row.original, 'shippingPrice', user?.role) >
                        0 && (
                        <TrendingUp className="absolute right-[-11px] top-[-11px] h-4 w-4 rounded-full bg-green-100 p-[2px] text-green-700" />
                      )}
                    {!isParent &&
                      user?.role &&
                      hasPreviousVersion(
                        row.original,
                        'shippingPrice',
                        user?.role
                      ) &&
                      getDeltaSign(row.original, 'shippingPrice', user?.role) <
                        0 && (
                        <TrendingDown className="absolute right-[-11px] top-[-11px] h-4 w-4 rounded-full bg-red-100 p-[2px] text-red-700" />
                      )}
                  </TablePriceDisplay>
                </div>
              </TooltipTrigger>
              {!isParent &&
                user?.role &&
                hasPreviousVersion(
                  row.original,
                  'shippingPrice',
                  user?.role
                ) && (
                  <TooltipContent>
                    <p className="text-sm font-semibold">
                      {t('kpi.shipping_price_modified')}
                    </p>
                    <p>
                      <span className="text-gray-500">
                        {t('general.previous')} :
                      </span>{' '}
                      <span>
                        {formatCurrency(
                          getMyLastValue(
                            row.original,
                            'shippingPrice',
                            user?.role
                          ),
                          store
                        )}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">
                        {t('general.current')} :
                      </span>{' '}
                      <span>
                        {formatCurrency(
                          getCurrentValue(row.original, 'shippingPrice'),
                          store
                        )}
                      </span>
                    </p>
                    <p className="font-semibold">
                      <span className="text-gray-500">
                        {t('general.gap')} :
                      </span>{' '}
                      <span
                        className={cn({
                          'text-red-700':
                            getDeltaValue(
                              row.original,
                              'shippingPrice',
                              user?.role
                            ) < 0,
                          'text-green-700':
                            getDeltaValue(
                              row.original,
                              'shippingPrice',
                              user?.role
                            ) > 0,
                        })}
                      >
                        {formatCurrency(
                          getDeltaValue(
                            row.original,
                            'shippingPrice',
                            user?.role
                          ),
                          store
                        )}
                      </span>
                    </p>
                  </TooltipContent>
                )}
            </Tooltip>
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefShippingPrice = ({
  handleBlur,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'shippingPrice';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'ShippingPrice',
      accessorFn: (row) => row.activeVersion?.shippingPrice ?? 0,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <InfoIcon className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('kpi.shipping_price_tooltip')}</p>
            </TooltipContent>
          </Tooltip>{' '}
          <div
            className="flex flex-1 items-center gap-2"
            onClick={() => setColumnSort(accessorKey)}
          >
            {t('kpi.shipping_price_long')} ({t(getVatLabelKey(store))})
            <SortIcon pointer="shippingPrice" />
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <ShippingPriceCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size: 180,
    }),
    [
      t,
      store,
      setColumnSort,
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    ]
  );
};

export default useColumnDefShippingPrice;
